import { get } from '@/util/api';
import { useQuery } from 'react-query';
import z from 'zod';
import { adItemSchema } from './ad';
import { dataArray, dataItem } from './helpers';
import { mailFrequencyOptionSchema, mailFrequencySchema } from './mail';

export const reportReasonSchema = z.object({
  id: z.number(),
  title: z.string(),
});

const pageSchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string(),
});

export const settingsSchema = z.object({
  id: z.number(),
  color_1: z.string(),
  color_2: z.string(),
  color_3: z.string(),
  color_4: z.string(),
  color_5: z.string(),
  color_6: z.string(),
  color_7: z.string(),
  color_8: z.string(),
  ad_report_reasons: dataArray(reportReasonSchema),
  footer_pages: dataArray(pageSchema),
  rules_page: dataItem(pageSchema).nullable(),
  what_is_page: dataItem(pageSchema).nullable(),
  contact_ad: dataItem(adItemSchema).nullable(),
});

export const illustrationNameSchema = z.enum([
  '404',
  'blikjestelefoon',
  'boodschappentassen',
  'cadeau',
  'handen_hartje',
  'handen',
  'hart',
  'identiteit',
  'laptop_bank',
  'laptop_closeup',
  'mannen_laptop',
  'schadelijke_activiteiten',
  'team',
  'visie',
  'vraag_hulp',
]);

export type SettingsType = z.infer<typeof settingsSchema>;
export type PageRefType = z.infer<typeof pageSchema>;
export type ReportReasonType = z.infer<typeof reportReasonSchema>;
export type IllustrationNameType = z.infer<typeof illustrationNameSchema>;
export type MailFrequencyType = z.infer<typeof mailFrequencySchema>;
export type MailFrequencyOptionType = z.infer<typeof mailFrequencyOptionSchema>;

export async function getSettings(): Promise<SettingsType> {
  const { data } = await get('/settings', dataItem(settingsSchema));
  return data;
}

export const mailFrequencyOptions: MailFrequencyOptionType[] = [
  {
    label: 'Direct',
    value: 'direct',
  },
  {
    label: 'Tussen 09:00 en 20:00',
    value: 'daily',
  },
  {
    label: 'Eenmaal per week',
    value: 'weekly',
  },
  {
    label: 'Nooit',
    value: 'never',
  },
];

const defaultSettings: SettingsType = {
  id: 1,
  color_1: '#41a5f7',
  color_2: '#94c3c5',
  color_3: '#91847d',
  color_4: '#edcbd4',
  color_5: '#c44b46',
  color_6: '#e28e54',
  color_7: '#788f76',
  color_8: '#e5d055',
  ad_report_reasons: {
    data: [],
  },
  footer_pages: {
    data: [],
  },
  rules_page: null,
  what_is_page: null,
  contact_ad: null,
};

// The query data is precached in _app.tsx. The default settings
// are primarily here so we don't have to deal with null checks.
export function useSettings(): SettingsType {
  const { data } = useQuery({
    queryKey: ['settings'],
    queryFn: () => getSettings(),
    staleTime: 1000 * 10,
  });
  return data || defaultSettings;
}
